import type {
  AdvertisementStatus,
  AdvertisingBudgetPaceRowDto,
  AdvertisingChannel,
} from "@/api/advertising";
import { MissingValue, StatusBadge } from "@/design-system/components/ui";
import { formatCurrency } from "@/design-system/lib/format";

/**
 * Advertising display vocabulary (WS6) — in one place so the ads table, the
 * editor and the roll-up cannot label the same value three ways.
 */

/** The picker's order and its human labels. The stored values are the C#
 *  constants; a channel added server-side must be added here or it renders as
 *  a raw token AND disappears from the dropdown. */
export const CHANNEL_OPTIONS: { value: AdvertisingChannel; label: string }[] = [
  // The four listing SITES first — where a buyer was actually reading — then
  // the paid channels, then the rest. Mirrors `AdvertisingChannels.All`.
  { value: "BizBuySell", label: "BizBuySell" },
  { value: "BizQuest", label: "BizQuest" },
  { value: "LoopNet", label: "LoopNet" },
  { value: "BusinessesForSale", label: "BusinessesForSale" },
  { value: "GoogleSearch", label: "Google Search" },
  { value: "Meta", label: "Meta" },
  { value: "YouTube", label: "YouTube" },
  { value: "LinkedIn", label: "LinkedIn" },
  { value: "Email", label: "Email" },
  { value: "DirectMail", label: "Direct mail" },
  { value: "Event", label: "Event" },
  { value: "Other", label: "Other" },
];

export const STATUS_OPTIONS: { value: AdvertisementStatus; label: string }[] = [
  { value: "Draft", label: "Draft" },
  { value: "Running", label: "Running" },
  { value: "Paused", label: "Paused" },
  { value: "Archived", label: "Archived" },
];

export function channelLabel(value: AdvertisingChannel | string): string {
  return CHANNEL_OPTIONS.find((c) => c.value === value)?.label ?? value;
}

/**
 * The status pill.
 *
 * <p>Running is the only "good" tone. Paused is neutral rather than a warning:
 * pausing is a deliberate act, and an amber pill would read as something having
 * gone wrong. Archived is muted — it is over, not broken.</p>
 */
export function AdvertisementStatusBadge({ status }: { status: AdvertisementStatus }) {
  const tone =
    status === "Running" ? "ok"
    : status === "Paused" ? "info"
    : status === "Draft" ? "neutral"
    : "muted";

  return <StatusBadge tone={tone}>{status}</StatusBadge>;
}

/**
 * A cost-per-outcome cell.
 *
 * <p>🔴 <b>Null renders an em dash, never `$0.00`.</b> The server returns null
 * when the denominator is zero, because an advertisement that has produced no
 * leads has no cost per lead — and `$0.00` in that cell says the opposite of
 * the truth about the worst-performing row on the page.</p>
 */
export function CostCell({ value }: { value?: number | null }) {
  if (value === null || value === undefined) return <MissingValue />;
  return <>{formatCurrency(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</>;
}

/**
 * A target price range as prose — `$500,000 – $1,200,000`, an open bound as
 * `From $500,000` / `Up to $1,200,000`, and null when neither bound is set so
 * the caller can render the em dash a plain missing value gets.
 */
export function formatPriceRange(
  min?: number | null,
  max?: number | null,
): string | null {
  const fmt = (v: number) =>
    formatCurrency(v, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  const hasMin = min !== null && min !== undefined;
  const hasMax = max !== null && max !== undefined;
  if (hasMin && hasMax) return `${fmt(min)} – ${fmt(max)}`;
  if (hasMin) return `From ${fmt(min)}`;
  if (hasMax) return `Up to ${fmt(max)}`;
  return null;
}

/**
 * The over-budget signal for one team-month, from the cost-per-lead report's
 * `budgets` rows.
 *
 * <p>🔴 <b>A flag, never a block.</b> The spend already happened; the badge
 * is what a manager opened the page to see. Amber (`warn`), not red: it is a
 * thing to look at this month, not a breached date (Table & Tile §6.4). Null
 * when the row is absent or under budget, so the caller renders NOTHING
 * rather than a reassuring pill — state-absent is prose, not a badge (§6.1).</p>
 */
export function OverBudgetBadge({ pace }: { pace: AdvertisingBudgetPaceRowDto | null | undefined }) {
  if (!pace?.isOverBudget) return null;
  return <StatusBadge tone="warn">Over budget</StatusBadge>;
}

/** Money that always exists — a spend total is zero, not unknown. */
export function money(value: number): string {
  return formatCurrency(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * Utilization, from the server's FRACTION.
 *
 * <p>Rounded to whole percent and NOT clamped: a team at 140% of budget must
 * read 140%, because the over-budget case is the one a manager opened the page
 * for. `formatProgressPercent` clamps to 100 and is therefore wrong here.</p>
 */
export function utilizationLabel(fraction?: number | null): string | null {
  if (fraction === null || fraction === undefined) return null;
  return `${Math.round(fraction * 100)}%`;
}

/** `YYYY-MM` for the month containing a date. */
export function periodOf(date: Date): string {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}`;
}

/** The current month, in the period form every advertising read is keyed by. */
export function currentPeriod(): string {
  return periodOf(new Date());
}

/**
 * `YYYY-MM` shifted by whole months.
 *
 * <p>Built from the numbers rather than from `Date.setMonth`, which clamps the
 * day-of-month and can therefore skip a month entirely — the trap that made a
 * `AddMonths(-1)` round trip lose a day in this repository's own tests.</p>
 */
export function shiftPeriod(period: string, months: number): string {
  const [y, m] = period.split("-").map(Number);
  if (!y || !m) return period;
  const zeroBased = y * 12 + (m - 1) + months;
  const year = Math.floor(zeroBased / 12);
  const month = (zeroBased % 12) + 1;
  return `${year}-${String(month).padStart(2, "0")}`;
}

/** `August 2026` from a `YYYY-MM` period. Formatted in UTC — a period is a
 *  wall-calendar fact and the viewer's timezone must never touch it. */
export function periodLabel(period: string): string {
  const [y, m] = period.split("-").map(Number);
  if (!y || !m) return period;
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString("en-US", {
    timeZone: "UTC",
    month: "long",
    year: "numeric",
  });
}

/** The first day of a period, as the `YYYY-MM-DD` the budget write expects. */
export function periodStartIso(period: string): string {
  return `${period}-01`;
}
